// Which revisions THIS device authored, so the local changes feed can tell a genuine
// unflushed local write from a copy of our own write arriving back through sync.
//
// It lives outside DatabaseWrapper because that class imports pouchdb-browser, which
// references `self` and cannot be loaded under node, so anything that wants a unit test is
// extracted into a module of its own.

/**
 * Tracks `${id}@${rev}` revisions that THIS app authored through its own local save path,
 * so the local changes feed can tell a genuine unflushed local write from a sync-pulled echo
 * (CouchDB replays our own just-pushed docs back through _changes carrying our deviceId) or a
 * pass-through graft — both of which are already on the server and must not look like pending
 * local writes. The set is consume-once (an entry is removed when the changes feed observes
 * it) and bounded by size + TTL for the rare write that never surfaces on the feed.
 *
 * Recording happens synchronously at the local put/bulkDocs response, strictly before the
 * write can surface on the changes feed, so there is no event-ordering race.
 */
export class LocalRevTracker {
  private revs = new Map<string, number>();

  constructor(
    private readonly maxEntries = 200,
    private readonly ttlMs = 60_000,
  ) {}

  /** Record a revision this app just wrote locally. `nowMs` is epoch milliseconds. */
  record(id: string | undefined, rev: string | undefined, nowMs: number): void {
    if (!id || !rev) return;
    // if at capacity, drop expired entries first, then evict oldest (Map keeps insertion order)
    if (this.revs.size >= this.maxEntries) {
      for (const [key, recordedMs] of this.revs) {
        if (nowMs - recordedMs > this.ttlMs) this.revs.delete(key);
      }
      while (this.revs.size >= this.maxEntries) {
        const oldest = this.revs.keys().next().value;
        if (oldest === undefined) break;
        this.revs.delete(oldest);
      }
    }
    this.revs.set(`${id}@${rev}`, nowMs);
  }

  /**
   * Return true (and consume the entry) if this exact `${id}@${rev}` was authored locally.
   * A miss means the change is a sync-pulled echo or a graft. The match is age-independent:
   * a genuine local write is genuine no matter how late the changes feed observes it (the
   * watchdog applies its own 30s staleness gate downstream). The record-side TTL only bounds
   * memory for entries that are never observed; it must not turn a real late observation into
   * a false echo. `nowMs` is kept in the signature for symmetry with record().
   */
  consume(id: string | undefined, rev: string | undefined, _nowMs: number): boolean {
    if (!id || !rev) return false;
    const key = `${id}@${rev}`;
    if (!this.revs.has(key)) return false;
    this.revs.delete(key);
    return true;
  }

  get size(): number {
    return this.revs.size;
  }
}
